import { Component, Show, createMemo, createSignal, onMount } from "solid-js";
import { getMe, logout, requestAccess } from "../api/auth";
import type { AuthMeResponse } from "../api/types";
import { logoSrc } from "../stores/themeStore";

export const PendingPage: Component = () => {
  const [loading, setLoading] = createSignal(true);
  const [user, setUser] = createSignal<AuthMeResponse | null>(null);
  const [requesting, setRequesting] = createSignal(false);
  const [requested, setRequested] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const isScreening = createMemo(() => user()?.hold_reason === "screening");

  onMount(async () => {
    try {
      const me = await getMe();
      if (me.status === "active") {
        window.location.href = "/board";
        return;
      }
      // If user already requested access (status is "pending"), show that state
      if (me.status === "pending") {
        setRequested(true);
      }
      setUser(me);
    } catch {
      // Session expired or invalid — redirect to login
      window.location.href = "/login";
      return;
    }
    setLoading(false);
  });

  const handleRequestAccess = async () => {
    setRequesting(true);
    setError(null);
    try {
      const result = await requestAccess();
      if (result.status === "pending") {
        setRequested(true);
      } else {
        setError("Failed to submit request. Please try again.");
      }
    } catch {
      setError("Failed to submit request. Please try again.");
    } finally {
      setRequesting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  return (
    <Show when={!loading()}>
      <div class="min-h-screen bg-paper flex items-center justify-center p-4">
        <div class="w-full max-w-md">
          {/* Logo and heading */}
          <div class="text-center mb-8">
            <img
              src={logoSrc()}
              alt="Flowicity"
              class="w-16 h-16 mx-auto mb-4 rounded-md"
            />
            <Show
              when={isScreening()}
              fallback={
                <>
                  <h1 class="text-2xl font-bold text-ink">
                    Signups are currently closed
                  </h1>
                  <p class="mt-2 text-sm text-ink-2">
                    We're gradually opening access to Flowicity. Join the
                    mailing list to get notified, or request early access below.
                  </p>
                </>
              }
            >
              <h1 class="text-2xl font-bold text-ink">
                We're reviewing your account
              </h1>
              <p class="mt-2 text-sm text-ink-2">
                This signup didn't clear our automated checks, so someone needs
                to take a look before your account opens. Signing in again won't
                change anything — we'll sort it out from our side.
              </p>
            </Show>
          </div>

          {/* User info card */}
          <Show when={user()}>
            {(u) => (
              <div class="bg-surface rounded-md border border-line p-6 mb-4">
                <div class="flex items-center gap-3 mb-5">
                  <Show
                    when={u().avatar_url}
                    fallback={
                      <div class="w-10 h-10 rounded-full bg-sunken flex items-center justify-center text-ink-3 font-medium">
                        {u().name?.charAt(0)?.toUpperCase() ?? "?"}
                      </div>
                    }
                  >
                    <img
                      src={u().avatar_url}
                      alt={u().name}
                      class="w-10 h-10 rounded-full"
                    />
                  </Show>
                  <div class="min-w-0">
                    <p class="text-sm font-medium text-ink truncate">
                      {u().name}
                    </p>
                    <p class="text-xs text-ink-3 truncate">
                      {u().github_username
                        ? `@${u().github_username}`
                        : u().email}
                    </p>
                  </div>
                </div>

                {/* Error message */}
                <Show when={error()}>
                  <div class="mb-4 p-3 bg-signal-red-tint border border-signal-red/25 rounded text-sm text-signal-red">
                    {error()}
                  </div>
                </Show>

                {/* Request access / confirmation / appeal */}
                <Show
                  when={!isScreening()}
                  fallback={
                    <p class="text-sm text-ink-2 text-center">
                      Think this is a mistake?{" "}
                      <a
                        href="mailto:legal@flowicity.com?subject=Flowicity%20account%20review"
                        class="text-ink font-medium underline focus-ring"
                      >
                        Email legal@flowicity.com
                      </a>
                    </p>
                  }
                >
                  <Show
                    when={!requested()}
                    fallback={
                      <div class="p-3 bg-signal-green-tint border border-signal-green/25 rounded text-sm text-signal-green text-center">
                        Request sent! We'll review your access shortly.
                      </div>
                    }
                  >
                    <button
                      onClick={handleRequestAccess}
                      disabled={requesting()}
                      class="w-full px-4 py-3 bg-ink text-paper rounded focus-ring hover:bg-ink-2 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {requesting() ? "Submitting..." : "Request early access"}
                    </button>
                  </Show>
                </Show>
              </div>
            )}
          </Show>

          {/* Mailing list card */}
          <Show when={!isScreening()}>
            <div class="bg-surface rounded-md border border-line p-6 mb-4">
              <h2 class="text-sm font-semibold text-ink mb-2">Stay updated</h2>
              <p class="text-sm text-ink-2 mb-3">
                Subscribe to hear when signups open and get product updates.
              </p>
              <a
                href="https://buttondown.com/flowicity"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center justify-center gap-2 w-full px-4 py-3 bg-ink text-paper rounded focus-ring hover:bg-ink-2 transition-colors font-medium text-sm"
              >
                Subscribe on Buttondown
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25"
                  />
                </svg>
              </a>
            </div>
          </Show>

          {/* Logout link */}
          <div class="text-center">
            <button
              onClick={handleLogout}
              class="text-sm text-ink-3 hover:text-ink transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>
      </div>
    </Show>
  );
};
